import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Crown, Trophy } from 'lucide-react';
import LeaderboardShareButton from './LeaderboardShareButton';
import { UserAvatar } from '@/components/UserAvatar';

interface LeaderboardEntry {
  id: string;
  user_id: string | null;
  display_name: string | null;
  total_points: number | null;
  correct_winners: number | null;
  correct_scores: number | null;
  rank: number | null;
  avatar_id?: number | null;
}

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  currentUserId?: string;
  totalSeries: number;
  groupName?: string;
  groupId: string;
}

interface PodiumColumnProps {
  entry: LeaderboardEntry;
  rank: 1 | 2 | 3;
  isCurrentUser: boolean;
  totalSeries: number;
  animationDelay: number;
  onClick: () => void;
}

const PODIUM_CONFIG = {
  1: {
    minHeight: 'min-h-[180px]',
    avatarSize: 'lg' as const,
    avatarBorder: 'border-2 border-amber-400',
    pointsSize: 'text-2xl',
    nameSize: 'text-sm font-bold',
    glow: 'shadow-[0_0_24px_-4px_hsl(24_95%_53%/0.15)]',
    showCrown: true,
    showStats: true,
    order: 'order-2',
  },
  2: {
    minHeight: 'min-h-[140px]',
    avatarSize: 'md' as const,
    avatarBorder: 'border-2 border-slate-300',
    pointsSize: 'text-lg',
    nameSize: 'text-xs font-bold',
    glow: '',
    showCrown: false,
    showStats: false,
    order: 'order-1',
  },
  3: {
    minHeight: 'min-h-[140px]',
    avatarSize: 'md' as const,
    avatarBorder: 'border-2 border-amber-600',
    pointsSize: 'text-lg',
    nameSize: 'text-xs font-bold',
    glow: '',
    showCrown: false,
    showStats: false,
    order: 'order-3',
  },
} as const;

const PodiumColumn = ({ entry, rank, isCurrentUser, totalSeries, animationDelay, onClick }: PodiumColumnProps) => {
  const config = PODIUM_CONFIG[rank];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: animationDelay, duration: 0.4 }}
      onClick={onClick}
      className={`${config.order} flex flex-col items-center justify-end gap-2 flex-1 ${config.minHeight} pb-4 px-2 rounded-2xl cursor-pointer ${config.glow} ${
        isCurrentUser ? 'bg-accent/5' : ''
      }`}
    >
      {config.showCrown && (
        <Crown className="w-5 h-5 text-amber-400 mb-1 flex-shrink-0" />
      )}

      <div className={`rounded-full p-0.5 ${config.avatarBorder} ${isCurrentUser ? 'ring-2 ring-accent/30' : ''}`}>
        <UserAvatar
          avatarId={entry.avatar_id ?? null}
          displayName={entry.display_name}
          size={config.avatarSize}
        />
      </div>

      <div className="text-center space-y-0.5">
        <p className={`${config.nameSize} leading-tight max-w-[80px] truncate`}>
          {entry.display_name}
        </p>
        <p className={`${config.pointsSize} text-accent font-bold tabular-nums`}>
          {entry.total_points || 0}
        </p>
        <p className="text-[10px] text-muted-foreground">נקודות</p>
        {config.showStats && (
          <div className="flex flex-col gap-0.5 mt-1">
            <span className="text-[10px] text-muted-foreground">
              {entry.correct_winners || 0}/{totalSeries} מנצחות
            </span>
            <span className="text-[10px] text-muted-foreground">
              {entry.correct_scores || 0}/{totalSeries} מדויקות
            </span>
          </div>
        )}
      </div>

      <div
        className={`w-full rounded-t-lg mt-auto ${
          rank === 1 ? 'h-10 bg-amber-400/20' : rank === 2 ? 'h-6 bg-slate-300/15' : 'h-4 bg-amber-600/15'
        }`}
      />
    </motion.div>
  );
};

const Leaderboard = ({ entries, currentUserId, totalSeries, groupName, groupId }: LeaderboardProps) => {
  const navigate = useNavigate();

  const handlePlayerClick = (userId: string | null) => {
    if (!userId) return;
    navigate(`/group/${groupId}/bets/${userId}?from=leaderboard`);
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-20">
        <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
        <p className="text-muted-foreground font-medium">ההימורים רק מתחילים 👑</p>
        <p className="text-xs text-muted-foreground mt-1">כשיהיו תוצאות, הטבלה תתעדכן</p>
      </div>
    );
  }

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  const first = top3[0];
  const second = top3[1] ?? null;
  const third = top3[2] ?? null;

  return (
    <div className="space-y-4">
      {groupName && (
        <div className="flex justify-end mb-2">
          <LeaderboardShareButton entries={entries} groupName={groupName} />
        </div>
      )}

      {/* Podium section */}
      <div className="flex items-end gap-2 justify-center px-2">
        {second && (
          <PodiumColumn
            entry={second}
            rank={2}
            isCurrentUser={second.user_id === currentUserId}
            totalSeries={totalSeries}
            animationDelay={0.1}
            onClick={() => handlePlayerClick(second.user_id)}
          />
        )}

        {first && (
          <PodiumColumn
            entry={first}
            rank={1}
            isCurrentUser={first.user_id === currentUserId}
            totalSeries={totalSeries}
            animationDelay={0}
            onClick={() => handlePlayerClick(first.user_id)}
          />
        )}

        {third && (
          <PodiumColumn
            entry={third}
            rank={3}
            isCurrentUser={third.user_id === currentUserId}
            totalSeries={totalSeries}
            animationDelay={0.2}
            onClick={() => handlePlayerClick(third.user_id)}
          />
        )}
      </div>

      {/* Rank 4+ list */}
      {rest.length > 0 && (
        <div className="space-y-2 mt-2">
          {rest.map((entry, i) => {
            const rank = entry.rank || i + 4;
            const isCurrentUser = entry.user_id === currentUserId;

            return (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i + 3) * 0.05, duration: 0.3 }}
                onClick={() => handlePlayerClick(entry.user_id)}
                className={`bg-card rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-card/80 transition-colors ${
                  isCurrentUser ? 'border border-accent/30 bg-accent/5' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold w-8 text-center text-muted-foreground tabular-nums">{rank}</span>
                  <UserAvatar avatarId={entry.avatar_id ?? null} displayName={entry.display_name} size="sm" />
                  <div>
                    <p className="font-semibold text-sm">{entry.display_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {entry.correct_winners || 0} מנצחות · {entry.correct_scores || 0} מדויקות
                    </p>
                  </div>
                </div>
                <span className="text-accent font-bold tabular-nums">{entry.total_points || 0}</span>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
